import * as React from 'react';

import { Dialog } from '@coursera/cds-core';

import EnrollmentDialogContent from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/EnrollmentDialogContent';
import type { CourseEnrollmentStatus } from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/EnrollmentDialogContent';
import { useTermStore } from 'bundles/degree-home/components/v3-dpm/context/TermStoreContext';
import type { TermStore, TermStoreEntry } from 'bundles/degree-home/components/v3-dpm/context/TermStoreContext';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { now } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useStudentSuccessAttributes from 'bundles/degree-home/components/v3-dpm/lib/useStudentSuccessAttributes';

import _t from 'i18n!nls/degree-home';

export const hasOpenRegistration = (term: TermStoreEntry): boolean => {
  if (!term.registrationStartDate || !term.registrationEndDate) {
    return false;
  }

  const currentTime = now();

  const startsInThePast = term.registrationStartDate <= currentTime;
  const endsInTheFuture = currentTime <= term.registrationEndDate;

  return startsInThePast && endsInTheFuture;
};

const registrationStartIsAfterToday = (term: TermStoreEntry): boolean => {
  if (!term.registrationStartDate) {
    return false;
  }

  return term.registrationStartDate > now();
};

const eitherRegistrationDateIsMissing = (term: TermStoreEntry) =>
  !term.registrationEndDate || !term.registrationStartDate;

/** Given a course and the degree-wide term collection, determine whether the course can currently be enrolled in */
export const courseEnrollmentStatus = (
  course: PersonalizedSegmentCourse,
  idIndexedTerms: TermStore
): CourseEnrollmentStatus => {
  const courseraCourse = course.segmentCourse.course.courseraCourse;
  const terms = course.segmentCourse.termAvailabilityPaginated?.elements;
  const termsWithLinkedTerm = terms?.filter((term) => term.linkedTerm?.id).map((term) => term.linkedTerm!) || [];

  if (!courseraCourse || !terms || terms.length === 0) {
    return 'not available';
  }

  const storeTerms = termsWithLinkedTerm.map(({ id }) => idIndexedTerms[id]);

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (storeTerms.some(hasOpenRegistration)) {
    return 'open';
  }

  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (storeTerms.some(registrationStartIsAfterToday)) {
    return 'closed';
  }

  // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (storeTerms.every(eitherRegistrationDateIsMissing)) {
    return 'unknown';
  } else {
    return 'not available';
  }
};

/** Given a course and the degree-wide term collection, determine what will be the date of the next enrollment period */
export const getNextEnrollmentStartDate = (course: PersonalizedSegmentCourse, idIndexedTerms: TermStore): string => {
  const courseraCourse = course.segmentCourse.course.courseraCourse;
  const terms = course.segmentCourse.termAvailability;
  const currentTime = now();

  if (!courseraCourse || !terms || terms.length === 0) {
    return '';
  }

  const storeTerms = terms.map(({ id }) => idIndexedTerms[id]);
  let nextEnrollmentPeriod: Date | undefined;

  // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  storeTerms.forEach(({ registrationStartDate }) => {
    if (!registrationStartDate || registrationStartDate < currentTime) {
      return;
    }

    if (!nextEnrollmentPeriod || registrationStartDate < nextEnrollmentPeriod) {
      nextEnrollmentPeriod = registrationStartDate;
    }
  });

  return nextEnrollmentPeriod ? nextEnrollmentPeriod.toISOString() : '';
};

interface Props {
  modalIsOpen: boolean;
  closeModal: () => void;
  course: PersonalizedSegmentCourse;
}
const EnrollmentDialog = ({ modalIsOpen, closeModal, course }: Props) => {
  const useCurriculumResult = useCurriculum();
  const { studentSuccessAttributes } = useStudentSuccessAttributes();

  const { terms } = useTermStore();

  const { segmentCourse } = course;

  const curriculumData = useCurriculumResult.data?.Degree?.queryById?.curriculum;

  /*
    Quick exit to avoid race condition where curriculum loads before terms
  */
  if (!curriculumData || terms === undefined) {
    return null;
  }

  const isStackable = segmentCourse.course.isPublicLiveCourse;

  const enrollmentStatus = courseEnrollmentStatus(course, terms);
  const nextEnrollmentPeriod = getNextEnrollmentStartDate(course, terms);

  return (
    <Dialog open={modalIsOpen} color="darkBlue" onClose={closeModal}>
      <Dialog.HeadingGroup>{_t('Enrollment options')}</Dialog.HeadingGroup>
      <EnrollmentDialogContent
        isStackable={isStackable}
        enrollmentUrl={studentSuccessAttributes?.enrollmentUrl || ''}
        courseSlug={segmentCourse.course.courseraCourse?.slug || ''}
        enrollmentStatus={enrollmentStatus}
        learnerAdmissionStatus={curriculumData.learnerAdmissionStatus}
        nextEnrollmentPeriod={nextEnrollmentPeriod}
        onOkButtonClick={closeModal}
      />
    </Dialog>
  );
};

export default EnrollmentDialog;
